import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeChapterOverviewSlideSkeleton } from "./TypeChapterOverviewSlide";
import type { TypeCooperationSlideSkeleton } from "./TypeCooperationSlide";
import type { TypeCustomSlideSkeleton } from "./TypeCustomSlide";
import type { TypeHighlightSlideSkeleton } from "./TypeHighlightSlide";
import type { TypeHomeSlideSkeleton } from "./TypeHomeSlide";
import type { TypeMaskedOverviewSlideSkeleton } from "./TypeMaskedOverviewSlide";
import type { TypeMediaSkeleton } from "./TypeMedia";
import type { TypeOfferASlideSkeleton } from "./TypeOfferASlide";
import type { TypeOfferBSlideSkeleton } from "./TypeOfferBSlide";
import type { TypeOfferCSlideSkeleton } from "./TypeOfferCSlide";
import type { TypeOfferDSlideSkeleton } from "./TypeOfferDSlide";
import type { TypeOfferESlideSkeleton } from "./TypeOfferESlide";
import type { TypeRoutePricesSlideSkeleton } from "./TypeRoutePricesSlide";
import type { TypeSingleMediaSlideSkeleton } from "./TypeSingleMediaSlide";
import type { TypeSkiRegionSlideSkeleton } from "./TypeSkiRegionSlide";
import type { TypeSlideSectionSkeleton } from "./TypeSlideSection";
import type { TypeSvgMapSkeleton } from "./TypeSvgMap";
import type { TypeThankYouSlideSkeleton } from "./TypeThankYouSlide";
import type { TypeTrainSkeleton } from "./TypeTrain";
import type { TypeTrainRouteSlideSkeleton } from "./TypeTrainRouteSlide";
import type { TypeTravelPassSlideSkeleton } from "./TypeTravelPassSlide";
import type { TypeTravelPassVipSlideSkeleton } from "./TypeTravelPassVipSlide";
import type { TypeWelcomeSlideSkeleton } from "./TypeWelcomeSlide";

/**
 * Fields type definition for content type 'TypeSlideChapter'
 * @name TypeSlideChapterFields
 * @type {TypeSlideChapterFields}
 * @memberof TypeSlideChapter
 */
export interface TypeSlideChapterFields {
	/**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
	internalTitle: EntryFieldTypes.Symbol;
	/**
     * Field type definition for field 'title' (Title)
     * @name Title
     * @localized true
     */
	title?: EntryFieldTypes.Symbol;
	/**
     * Field type definition for field 'items' (Items)
     * @name Items
     * @localized false
     */
	items?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeChapterOverviewSlideSkeleton | TypeCooperationSlideSkeleton | TypeCustomSlideSkeleton | TypeHighlightSlideSkeleton | TypeHomeSlideSkeleton | TypeMaskedOverviewSlideSkeleton | TypeOfferASlideSkeleton | TypeOfferBSlideSkeleton | TypeOfferCSlideSkeleton | TypeOfferDSlideSkeleton | TypeOfferESlideSkeleton | TypeRoutePricesSlideSkeleton | TypeSingleMediaSlideSkeleton | TypeSkiRegionSlideSkeleton | TypeSlideSectionSkeleton | TypeThankYouSlideSkeleton | TypeTrainRouteSlideSkeleton | TypeTravelPassSlideSkeleton | TypeTravelPassVipSlideSkeleton | TypeWelcomeSlideSkeleton>>;
	/**
     * Field type definition for field 'image' (Image)
     * @name Image
     * @localized false
     */
	image: EntryFieldTypes.EntryLink<TypeMediaSkeleton>;
	/**
     * Field type definition for field 'svgId' (SVG Id)
     * @name SVG Id
     * @localized false
     * @summary An id which is used to link the chapter when referenced in SVG files
     */
	svgId?: EntryFieldTypes.Symbol;
	/**
     * Field type definition for field 'gettingThereImage' ("Getting There" Image)
     * @name "Getting There" Image
     * @localized false
     */
	gettingThereImage?: EntryFieldTypes.EntryLink<TypeMediaSkeleton>;
	/**
     * Field type definition for field 'gettingThereMap' ("Getting There" Map)
     * @name "Getting There" Map
     * @localized false
     */
	gettingThereMap?: EntryFieldTypes.EntryLink<TypeSvgMapSkeleton>;
	/**
     * Field type definition for field 'gettingThereTrains' ("Getting There" Trains)
     * @name "Getting There" Trains
     * @localized false
     */
	gettingThereTrains?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeTrainSkeleton>>;
}

/**
 * Entry skeleton type definition for content type 'slideChapter' (Chapter)
 * @name TypeSlideChapterSkeleton
 * @type {TypeSlideChapterSkeleton}
 * @author 7f4eh1nUoNcgKbPpHmyy5G
 * @since 2019-04-18T06:17:53.159Z
 * @version 83
 */
export type TypeSlideChapterSkeleton = EntrySkeletonType<TypeSlideChapterFields, "slideChapter">;
/**
 * Entry type definition for content type 'slideChapter' (Chapter)
 * @name TypeSlideChapter
 * @type {TypeSlideChapter}
 * @author 7f4eh1nUoNcgKbPpHmyy5G
 * @since 2019-04-18T06:17:53.159Z
 * @version 83
 */
export type TypeSlideChapter<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeSlideChapterSkeleton, Modifiers, Locales>;

export function isTypeSlideChapter<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeSlideChapter<Modifiers, Locales> {
	return entry.sys.contentType.sys.id === 'slideChapter';
}
